// SupercellScene.tsx
// 超胞展开动画（移植自 system0 kwj_front AtomsViewer/SupercellScene.jsx）。
// 晶胞逐方向扩展：1×1×1 → 2×1×1 → 2×2×1 → 2×2×2（n=2）。
// 原子/键按首次出现的阶段分组，新阶段后半程淡入；晶胞框平滑长大。
// 循环：展开 3s → 停 1.2s → 收缩 3s → 停 0.4s。
//
// 与 kwj 版的差异：BCS 的 Bond 带显式 fromPos/toPos（toPos 可能是 MIC 周期像
// 位置），复制超胞时需先恢复键端点的整数周期平移，再判断该键在当前 cell copy
// 中是否落在 [0,n) 范围内。晶胞框不按 group.scale 缩放（非正交晶格下 cartesian
// 缩放 ≠ na*a+nb*b+nc*c），改为每帧按插值向量重建 12 条棱。

import React, { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Sphere } from "@react-three/drei";
import * as THREE from "three";
import { getElementColor, type ColorScheme } from "./elementColors";
import { getElementRadius } from "./elementRadii";
import type { Atom } from "./structureParser";
import { BondsMesh, type RichBond } from "./BondsMesh";

// ── Animation timing (seconds) ──────────────────────────────────────────
const ANIMATION_TIME = 3.0;   // expand or contract
const PAUSE_FULL = 1.2;       // hold at full supercell
const PAUSE_SINGLE = 0.4;     // hold at single cell (brief)
const CYCLE_LENGTH = 2 * ANIMATION_TIME + PAUSE_FULL + PAUSE_SINGLE;

// Smallest opacity delta that triggers a material update.
const OPACITY_EPSILON = 0.01;

type CellOffset = [number, number, number];

interface ScAtom extends Atom {
  cellOffset: CellOffset;
  stage: number;
}

interface ScBond extends RichBond {
  stage: number;
}

export interface SupercellSpec {
  stages: { na: number; nb: number; nc: number }[];
  /** 每个阶段首次出现的原子/键。 */
  stageGroups: { atoms: ScAtom[]; bonds: ScBond[] }[];
  /** 全部超胞原子（用于相机取景）。 */
  allAtoms: ScAtom[];
  /** 超胞晶格（行 = n*a / n*b / n*c，9 个数）。 */
  supercellLattice: number[];
  n: number;
}

/** buildStages(2): [(1,1,1), (2,1,1), (2,2,1), (2,2,2)] */
function buildStages(n: number) {
  const stages: { na: number; nb: number; nc: number }[] = [];
  let na = 1, nb = 1, nc = 1;
  stages.push({ na, nb, nc });
  for (let i = 2; i <= n; i++) { na = i; stages.push({ na, nb, nc }); }
  for (let i = 2; i <= n; i++) { nb = i; stages.push({ na, nb, nc }); }
  for (let i = 2; i <= n; i++) { nc = i; stages.push({ na, nb, nc }); }
  return stages;
}

/** Index of the stage at which cell offset (i,j,k) first appears. */
function atomStage(cellOffset: CellOffset, stages: { na: number; nb: number; nc: number }[]): number {
  const [i, j, k] = cellOffset;
  for (let s = 0; s < stages.length; s++) {
    if (i < stages[s].na && j < stages[s].nb && k < stages[s].nc) return s;
  }
  return stages.length - 1;
}

/** 3x3 求逆；退化返回 null。 */
function invert3(m: number[][]): number[][] | null {
  const [a, b, c] = m[0], [d, e, f] = m[1], [g, h, i2] = m[2];
  const det = a * (e * i2 - f * h) - b * (d * i2 - f * g) + c * (d * h - e * g);
  if (Math.abs(det) < 1e-12) return null;
  const inv = 1 / det;
  return [
    [(e * i2 - f * h) * inv, (c * h - b * i2) * inv, (b * f - c * e) * inv],
    [(f * g - d * i2) * inv, (a * i2 - c * g) * inv, (c * d - a * f) * inv],
    [(d * h - e * g) * inv, (b * g - a * h) * inv, (a * e - b * d) * inv],
  ];
}

/**
 * 从单胞结构构建 n×n×n 超胞。键按端点的周期平移复制：键端 toPos = 原子位置 +
 * t·lattice（MIC 像），cell copy (i,j,k) 中该键保留当且仅当 i+t 的每个分量都在
 * [0,n) 内——与 kwj 版按 siteIndex 查表映射的语义一致。
 * 中点去重：显示层判键可能对同一物理键在原胞侧/镜像侧各画一次，超胞里会重叠。
 */
export function buildSupercell(
  atoms: Atom[],
  lattice: number[],
  bonds: RichBond[],
  n: number,
): SupercellSpec | null {
  if (n <= 1 || !lattice || lattice.length !== 9 || !atoms.length) return null;

  // 行 = a/b/c 向量；Cartesian = 行向量组合（与 structureParser 约定一致）。
  const M = [
    [lattice[0], lattice[3], lattice[6]],
    [lattice[1], lattice[4], lattice[7]],
    [lattice[2], lattice[5], lattice[8]],
  ];
  const Minv = invert3(M);
  if (!Minv) return null;

  // 恢复键端点的整数周期平移 t：pos = atoms[idx] + t·lattice。
  const transOf = (pos: [number, number, number], idx: number): CellOffset | null => {
    const a0 = atoms[idx];
    if (!a0) return null;
    const dx = pos[0] - a0.x, dy = pos[1] - a0.y, dz = pos[2] - a0.z;
    const fx = Minv[0][0] * dx + Minv[0][1] * dy + Minv[0][2] * dz;
    const fy = Minv[1][0] * dx + Minv[1][1] * dy + Minv[1][2] * dz;
    const fz = Minv[2][0] * dx + Minv[2][1] * dy + Minv[2][2] * dz;
    const tx = Math.round(fx), ty = Math.round(fy), tz = Math.round(fz);
    if (Math.abs(fx - tx) > 0.05 || Math.abs(fy - ty) > 0.05 || Math.abs(fz - tz) > 0.05) {
      return null; // 不是周期像位置（不该发生），保守跳过
    }
    return [tx, ty, tz];
  };

  const stages = buildStages(n);
  const seenBondKeys = new Set<string>();
  const stageGroups = stages.map(() => ({ atoms: [] as ScAtom[], bonds: [] as ScBond[] }));
  const allAtoms: ScAtom[] = [];

  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      for (let k = 0; k < n; k++) {
        const offX = i * lattice[0] + j * lattice[3] + k * lattice[6];
        const offY = i * lattice[1] + j * lattice[4] + k * lattice[7];
        const offZ = i * lattice[2] + j * lattice[5] + k * lattice[8];

        for (const atom of atoms) {
          const cellOffset: CellOffset = [i, j, k];
          const stage = atomStage(cellOffset, stages);
          const copy: ScAtom = {
            ...atom,
            x: atom.x + offX,
            y: atom.y + offY,
            z: atom.z + offZ,
            cellOffset,
            stage,
          };
          allAtoms.push(copy);
          stageGroups[stage].atoms.push(copy);
        }

        for (const bond of bonds) {
          const tF = transOf(bond.fromPos, bond.from);
          const tT = transOf(bond.toPos, bond.to);
          if (!tF || !tT) continue;
          const gF: CellOffset = [i + tF[0], j + tF[1], k + tF[2]];
          const gT: CellOffset = [i + tT[0], j + tT[1], k + tT[2]];
          if (gF.some((v) => v < 0 || v >= n) || gT.some((v) => v < 0 || v >= n)) continue;

          const x1 = bond.fromPos[0] + offX, y1 = bond.fromPos[1] + offY, z1 = bond.fromPos[2] + offZ;
          const x2 = bond.toPos[0] + offX, y2 = bond.toPos[1] + offY, z2 = bond.toPos[2] + offZ;

          // 中点去重（0.01Å 网格）：镜像判键造成的重复物理键只画一次。
          const mx = Math.round(((x1 + x2) / 2) * 100);
          const my = Math.round(((y1 + y2) / 2) * 100);
          const mz = Math.round(((z1 + z2) / 2) * 100);
          const key = `${mx},${my},${mz}`;
          if (seenBondKeys.has(key)) continue;
          seenBondKeys.add(key);

          const stage = Math.max(atomStage(gF, stages), atomStage(gT, stages));
          stageGroups[stage].bonds.push({
            ...bond,
            fromPos: [x1, y1, z1],
            toPos: [x2, y2, z2],
            stage,
          });
        }
      }
    }
  }

  const supercellLattice = lattice.map((v, idx) => v * n);

  return { stages, stageGroups, allAtoms, supercellLattice, n };
}

// ── Supercell atom sphere ───────────────────────────────────────────────

const SupercellAtom: React.FC<{
  atom: ScAtom;
  scale: number;
  colorScheme: ColorScheme;
}> = ({ atom, scale, colorScheme }) => {
  const radius = getElementRadius(atom.element) * scale;
  return (
    <Sphere position={[atom.x, atom.y, atom.z]} args={[radius, 32, 32]}>
      <meshStandardMaterial
        color={getElementColor(atom.element, colorScheme)}
        metalness={0.2}
        roughness={0.45}
      />
    </Sphere>
  );
};

// ── Animated cell frame (12 edges rebuilt per frame from interpolated vectors) ──

const FRAME_EDGES: [number, number][] = [
  [0, 1], [0, 2], [0, 3],
  [1, 4], [1, 5],
  [2, 4], [2, 6],
  [3, 5], [3, 6],
  [4, 7], [5, 7], [6, 7],
];

// ── Main SupercellScene ─────────────────────────────────────────────────

interface SupercellSceneProps {
  spec: SupercellSpec;
  atomRadiusScale: number;
  colorScheme: ColorScheme;
}

export const SupercellScene: React.FC<SupercellSceneProps> = ({ spec, atomRadiusScale, colorScheme }) => {
  const { stages, stageGroups } = spec;
  const progressRef = useRef(0);
  const stageGroupRefs = useRef<(THREE.Group | null)[]>([]);
  const prevOpacitiesRef = useRef<number[] | null>(null);

  // 原始（未放大）晶格向量；倍数由动画每帧提供。
  const a = useMemo(() => new THREE.Vector3(
    spec.supercellLattice[0] / spec.n, spec.supercellLattice[1] / spec.n, spec.supercellLattice[2] / spec.n,
  ), [spec]);
  const b = useMemo(() => new THREE.Vector3(
    spec.supercellLattice[3] / spec.n, spec.supercellLattice[4] / spec.n, spec.supercellLattice[5] / spec.n,
  ), [spec]);
  const c = useMemo(() => new THREE.Vector3(
    spec.supercellLattice[6] / spec.n, spec.supercellLattice[7] / spec.n, spec.supercellLattice[8] / spec.n,
  ), [spec]);

  // 晶胞框：8 顶点 12 棱的索引几何，position 每帧按插值向量重写。
  const frameGeo = useMemo(() => {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(new Float32Array(8 * 3), 3));
    geo.setIndex(FRAME_EDGES.flatMap(([i, j]) => [i, j]));
    return geo;
  }, []);
  const updateFrame = (na: number, nb: number, nc: number) => {
    const verts = [
      new THREE.Vector3(0, 0, 0),
      a.clone().multiplyScalar(na),
      b.clone().multiplyScalar(nb),
      c.clone().multiplyScalar(nc),
      a.clone().multiplyScalar(na).add(b.clone().multiplyScalar(nb)),
      a.clone().multiplyScalar(na).add(c.clone().multiplyScalar(nc)),
      b.clone().multiplyScalar(nb).add(c.clone().multiplyScalar(nc)),
      a.clone().multiplyScalar(na).add(b.clone().multiplyScalar(nb)).add(c.clone().multiplyScalar(nc)),
    ];
    const pos = frameGeo.getAttribute("position") as THREE.BufferAttribute;
    verts.forEach((v, i) => pos.setXYZ(i, v.x, v.y, v.z));
    pos.needsUpdate = true;
  };

  useFrame((_, delta) => {
    // Cycle: expand -> pause (full) -> contract -> pause (single) -> repeat
    progressRef.current += delta;
    const cyclePos = progressRef.current % CYCLE_LENGTH;

    let t: number;
    if (cyclePos < ANIMATION_TIME) {
      t = cyclePos / ANIMATION_TIME;                                        // expand 0->1
    } else if (cyclePos < ANIMATION_TIME + PAUSE_FULL) {
      t = 1;                                                                // hold at full
    } else if (cyclePos < 2 * ANIMATION_TIME + PAUSE_FULL) {
      t = 1 - (cyclePos - ANIMATION_TIME - PAUSE_FULL) / ANIMATION_TIME;    // contract 1->0
    } else {
      t = 0;                                                                // hold at single
    }

    const numStages = stages.length;
    const stageFloat = t * (numStages - 1);
    const currentStage = Math.min(Math.floor(stageFloat), numStages - 2);
    const stageProgress = stageFloat - currentStage;

    // Per-stage opacity: fade in during the second half of each stage transition.
    for (let s = 0; s < numStages; s++) {
      const group = stageGroupRefs.current[s];
      if (!group) continue;

      let targetOpacity: number;
      if (s === 0 || s <= currentStage) {
        targetOpacity = 1;
      } else if (s === currentStage + 1) {
        targetOpacity = Math.max(0, Math.min(1, (stageProgress - 0.5) * 2));
      } else {
        targetOpacity = 0;
      }

      const prev = prevOpacitiesRef.current?.[s];
      if (prev === undefined || Math.abs(targetOpacity - prev) > OPACITY_EPSILON) {
        group.visible = targetOpacity > OPACITY_EPSILON;
        group.traverse((child) => {
          const mesh = child as THREE.Mesh;
          if (mesh.material) {
            const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
            mats.forEach((m) => {
              m.transparent = targetOpacity < 1;
              m.opacity = targetOpacity;
            });
          }
        });
        if (!prevOpacitiesRef.current) prevOpacitiesRef.current = [];
        prevOpacitiesRef.current[s] = targetOpacity;
      }
    }

    // Cell frame: interpolate lattice-vector multipliers (non-orthogonal safe).
    const s1 = stages[currentStage];
    const s2 = stages[Math.min(currentStage + 1, numStages - 1)];
    const na = s1.na + (s2.na - s1.na) * stageProgress;
    const nb = s1.nb + (s2.nb - s1.nb) * stageProgress;
    const nc = s1.nc + (s2.nc - s1.nc) * stageProgress;
    updateFrame(na, nb, nc);
  });

  return (
    <group>
      {stageGroups.map((stage, stageIdx) => (
        <group
          key={stageIdx}
          ref={(el) => { stageGroupRefs.current[stageIdx] = el; }}
          visible={stageIdx === 0}
        >
          {stage.atoms.map((atom, idx) => (
            <SupercellAtom
              key={`sc-atom-${stageIdx}-${idx}`}
              atom={atom}
              scale={atomRadiusScale}
              colorScheme={colorScheme}
            />
          ))}
          {stage.bonds.length > 0 && (
            <BondsMesh bonds={stage.bonds} colorScheme={colorScheme} />
          )}
        </group>
      ))}

      <lineSegments geometry={frameGeo}>
        <lineBasicMaterial color="#00cc44" />
      </lineSegments>
    </group>
  );
};

export default SupercellScene;
